import { Component, input, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Environment, EnvironmentService } from '@galaxy/core';
import { TranslateService } from '@ngx-translate/core';
import { EditionChange, EditionUpgradeAction } from '@vendasta/marketplace-apps';
import { RetailPrice } from '@vendasta/marketplace-packages';
import { UpgradeCTADialogComponent, UpgradeCTADialogConfig } from '@vendasta/uikit';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import {
  APP_ID,
  DEMO_PAID_EDITION,
  FacebookSourceID,
  GoogleSourceID,
  InstagramSourceID,
  PROD_PAID_EDITION,
  SyncData,
  SyncStyle,
} from '../../../local-seo';
import {
  appID,
  PartnerMarket,
  UpgradeCTADialogService,
  UpgradeRedirectItem,
} from '../../../upgrade-cta-dialog/upgrade-cta-dialog.service';
import { ConnectedAccountModalComponent } from '../../connected-account-modal/connected-account-modal.component';
import { ListingAccuracyText } from '../../listing-sync-table/columns/presence-accuracy/accuracy-text.pipe';
import { ListingSidebarComponent } from '../../listing-sync-table/listing-sidebar/listing-sidebar.component';
import { ListingSyncService } from '../../listing-sync.service';
import { BadgeColor } from '@vendasta/galaxy/badge';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'local-seo-primary-source',
  templateUrl: './primary-source.component.html',
  styleUrls: ['./primary-source.component.scss'],
  standalone: false,
})
export class PrimarySourceComponent implements OnInit, OnDestroy {
  data = input.required<SyncData>();
  @Input() sidebarTopPadding = 0;
  @Input() isListingfromEmbedded = false;

  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('listingSidebar') listingSidebar: ListingSidebarComponent;

  data$: Observable<SyncData> = toObservable(this.data);
  syncStatusloading$: Observable<boolean>;
  subscriptions: Subscription[] = [];
  dialogRef: MatDialogRef<UpgradeCTADialogComponent>;
  accuracyText = ListingAccuracyText;
  protected readonly InstagramSourceID = InstagramSourceID;

  constructor(
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private upgradeDialogService: UpgradeCTADialogService,
    private env: EnvironmentService,
    private listingSyncService: ListingSyncService,
  ) {
    this.syncStatusloading$ = combineLatest([this.listingSyncService.loadingSource$, this.data$]).pipe(
      map(([loadingSource, data]: [number, SyncData]) => loadingSource === data.sourceId),
    );
  }

  syncStyleToBadgeColor(style: SyncStyle): BadgeColor {
    switch (style) {
      case SyncStyle.SUCCESS:
        return 'green';
      case SyncStyle.WARNING:
        return 'yellow';
      case SyncStyle.NEUTRAL:
        return 'blue-solid';
      default:
        return 'grey';
    }
  }

  ngOnInit(): void {
    this.subscriptions.push(
      combineLatest([this.data$, this.route.queryParams]).subscribe(([data, params]: [SyncData, Params]) => {
        if (params['service'] && serviceToSourceID(params['service']) === data.sourceId) {
          this.dialog
            .open(ConnectedAccountModalComponent, {
              data: {
                sourceId: data.sourceId,
                accountName: data.account?.username,
              },
            })
            .afterClosed()
            .pipe(
              switchMap((shouldSync) => {
                return this.listingSyncService.updateSyncing(data.sourceId, data.account.connectionId, shouldSync);
              }),
            )
            .subscribe(() => {
              this.router.navigate([this.router.url.split('?')[0]]);
            });
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  close() {
    this.sidenav.close();
  }

  open(locked: boolean) {
    if (!locked) {
      this.sidenav.open();
    } else {
      this.OpenUpgradeDialog();
    }
  }

  OpenUpgradeDialog() {
    const editionChangeDetails$ = this.upgradeDialogService.getEditionChangeDetails$();
    const upgradePrice$: Observable<RetailPrice> = combineLatest([
      this.upgradeDialogService.partnerMarket$,
      editionChangeDetails$,
    ]).pipe(
      switchMap(([pmConfig, editionChangeDetails]: [PartnerMarket, EditionChange]) => {
        let paidEdition = DEMO_PAID_EDITION;
        if (this.env.getEnvironment() == Environment.PROD) {
          paidEdition = PROD_PAID_EDITION;
        }
        return this.upgradeDialogService.getUpgradePrice$(pmConfig, appID, editionChangeDetails, paidEdition);
      }),
      shareReplay(1),
    );
    const formattedUpgradePrice$ = upgradePrice$.pipe(
      switchMap((upgradePrice) => this.upgradeDialogService.getFormattedRetailPrice$(upgradePrice)),
    );
    combineLatest([
      upgradePrice$,
      formattedUpgradePrice$,
      editionChangeDetails$,
      this.upgradeDialogService.getCustomUpgradeModalContent$(),
      this.upgradeDialogService.appSettings$,
    ])
      .pipe(
        take(1),
        switchMap(
          ([upgradePrice, formattedUpgradePrice, editionChangeDetails, customUpgradeModalContent, settings]) => {
            const marketingPoints = [
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_1'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_2'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_3'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_4'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_5'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_6'),
            ];
            const appName =
              settings.branding &&
              settings.branding.enabled &&
              settings.branding.enabled === true &&
              settings.branding.name
                ? settings.branding.name
                : 'Local SEO';
            let ctaText =
              !!customUpgradeModalContent && customUpgradeModalContent.buttonText
                ? customUpgradeModalContent.buttonText
                : undefined;
            if (editionChangeDetails?.editionUpgradeAction && !ctaText) {
              switch (editionChangeDetails?.editionUpgradeAction) {
                case EditionUpgradeAction.EDITION_UPGRADE_ACTION_CONTACT_SALES:
                  ctaText = this.translate.instant('UPGRADE_DIALOG.PRICE.CONTACT_SALES');
                  break;
                default:
                  ctaText = this.translate.instant('LISTING_SYNC.LSP_ADDON.UPGRADE_MODAL.BUTTON_TEXT');
                  break;
              }
            }
            const dialogConfig: UpgradeCTADialogConfig = {
              editionName: `${appName} ${this.translate.instant('COMMON.PRO')}`,
              imageText:
                !!customUpgradeModalContent && customUpgradeModalContent.titleText
                  ? customUpgradeModalContent.titleText
                  : this.translate.instant('EDITION.UPGRADE_MODAL.IMAGE_TEXT'),
              imageUrl:
                !!customUpgradeModalContent && customUpgradeModalContent.imageUrl
                  ? customUpgradeModalContent.imageUrl
                  : `https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/LocalSearch.svg`,
              keySellingPoints:
                !!customUpgradeModalContent && customUpgradeModalContent.sellingPoints
                  ? customUpgradeModalContent.sellingPoints
                  : marketingPoints,
              ctaText: ctaText || this.translate.instant('LISTING_SYNC.LSP_ADDON.UPGRADE_MODAL.BUTTON_TEXT'),
              upgradeNowText: this.upgradeDialogService.getUpgradeText(upgradePrice, editionChangeDetails),
              price: formattedUpgradePrice,
              frequency: this.upgradeDialogService.getFrequencyText(upgradePrice),
            };

            this.dialogRef = this.dialog.open(UpgradeCTADialogComponent, {
              data: dialogConfig,
            });
            return this.dialogRef.afterClosed().pipe(
              filter((upgradeClicked) => !!upgradeClicked),
              switchMap(() => {
                const isCustomPackage =
                  editionChangeDetails.editionUpgradeAction == EditionUpgradeAction.EDITION_UPGRADE_ACTION_CUSTOM_ORDER;
                let paidEdition = DEMO_PAID_EDITION;
                if (this.env.getEnvironment() == Environment.PROD) {
                  paidEdition = PROD_PAID_EDITION;
                }
                const item: UpgradeRedirectItem = isCustomPackage
                  ? { packageId: editionChangeDetails.priceReferencePackageId }
                  : { appId: APP_ID, editionId: paidEdition };
                return this.upgradeDialogService.buildBusinessCenterUpgradeRedirectInfo(
                  item,
                  editionChangeDetails.editionUpgradeAction,
                );
              }),
            );
          },
        ),
      )
      .subscribe((redirectInfo) => {
        this.upgradeDialogService.sendToVbc(redirectInfo);
      });
  }

  getSideNavTopGap(): number {
    return this.isListingfromEmbedded ? 124 : 100;
  }
}

function serviceToSourceID(service: string): number {
  switch (service) {
    case 'GMB_LOCATION':
      return GoogleSourceID;
    case 'FB_PAGE':
      return FacebookSourceID;
    default:
      return 10060;
  }
}
